﻿import { createContext, useEffect } from "react"
import { supabase } from "../lib/supabase"
import { useAppStore } from "../store/useAppStore"

export const UserContext = createContext()

export function UserProvider({ children }) {
  const user = useAppStore((state) => state.user)
  const userRole = useAppStore((state) => state.userRole)
  const businessId = useAppStore((state) => state.businessId)
  const business = useAppStore((state) => state.business)
  const loading = useAppStore((state) => state.loading)
  const error = useAppStore((state) => state.error)
  const setAuthSnapshot = useAppStore((state) => state.setAuthSnapshot)
  const clearAuthSnapshot = useAppStore((state) => state.clearAuthSnapshot)
  const setAuthLoading = useAppStore((state) => state.setAuthLoading)
  const setAuthError = useAppStore((state) => state.setAuthError)

  useEffect(() => {
    let mounted = true

    const resolveUserSnapshot = async (authUser) => {
      // Add timeout protection to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("User data fetch timeout")), 8000)
      })

      const fetchPromise = supabase
        .from("users")
        .select("*, businesses(*)")
        .eq("id", authUser.id)
        .maybeSingle()

      try {
        const { data: userDataResponse } = await Promise.race([fetchPromise, timeoutPromise])

        if (userDataResponse) {
          const businessId = userDataResponse.business_id || null
          return {
            user: userDataResponse,
            userRole: userDataResponse.role || "cashier",
            businessId,
            business:
              userDataResponse.businesses ||
              (businessId ? { id: businessId } : null),
          }
        }
      } catch (error) {
        console.warn("[UserContext] Failed to fetch user data, using metadata fallback:", error.message)
      }

      const currentState = useAppStore.getState()
      const currentUserId = currentState.user?.id || null
      if (currentUserId && currentUserId === authUser.id) {
        console.warn("[UserContext] Preserving current auth snapshot while profile fetch is unavailable")
        return {
          user: currentState.user,
          userRole: currentState.userRole,
          businessId: currentState.businessId,
          business: currentState.business,
        }
      }

      // Fallback to metadata if query fails or times out
      const metadata = authUser.user_metadata || {}
      return {
        user: authUser,
        userRole: metadata.business_name ? "owner" : "cashier",
        businessId: metadata.business_id || null,
        business: null,
      }
    }

    const syncAuth = async () => {
      try {
        console.log("[UserContext] Starting auth sync")
        const { data: { session } } = await supabase.auth.getSession()
        if (!mounted) return

        if (session?.user) {
          console.log("[UserContext] Session found, resolving user snapshot")
          const snapshot = await resolveUserSnapshot(session.user)
          if (mounted) {
            setAuthSnapshot(snapshot)
            console.log("[UserContext] Auth snapshot set successfully", {
              businessId: snapshot.businessId,
              role: snapshot.userRole,
            })
          }
        } else {
          console.log("[UserContext] No session, clearing auth")
          clearAuthSnapshot()
        }
      } catch (error) {
        console.error("[UserContext] Auth sync error:", error)
        if (mounted) {
          setAuthError(error?.message || "Failed to sync auth state")
          clearAuthSnapshot()
        }
      } finally {
        if (mounted) {
          setAuthLoading(false)
          console.log("[UserContext] Auth loading set to false")
        }
      }
    }

    const syncHandle = window.setTimeout(syncAuth, 0)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log("[UserContext] Auth state change event:", _event)
      if (!mounted) return

      if (session?.user) {
        try {
          const snapshot = await resolveUserSnapshot(session.user)
          if (mounted) {
            setAuthSnapshot(snapshot)
            console.log("[UserContext] Auth snapshot updated on state change")
          }
        } catch (error) {
          console.error("[UserContext] Failed to refresh auth state on change:", error)
          if (mounted) setAuthError(error?.message || "Failed to refresh auth state")
        }
      } else {
        console.log("[UserContext] No session on state change, clearing auth")
        clearAuthSnapshot()
      }
    })

    return () => {
      mounted = false
      window.clearTimeout(syncHandle)
      subscription.unsubscribe()
    }
  }, [clearAuthSnapshot, setAuthError, setAuthLoading, setAuthSnapshot])

  return <UserContext.Provider value={{ user, business, userRole, businessId, loading, error }}>{children}</UserContext.Provider>
}
